import React, { useState } from "react";
import { Modal } from "antd";
import { useNavigate } from "react-router-dom";
import { BarChart3, BookOpen, CheckCircle2, ClipboardList, Download, FileSpreadsheet, FileText, FileUp, GitCompareArrows, Save, Search, Settings, Sparkles } from "lucide-react";
import styles from "../styles.module.css";
import ProfileControl from "../component/ProfileControl";

const ANALYSIS_GUIDE = [
    { title: "Загрузите и обработайте", short: "Запрос или предложения", icon: FileUp, text: "Откройте вкладку «Обработка документа». Выберите «Обработать запрос» или «Обработать предложение», добавьте текст либо Excel-файл и нажмите «Обработать»." },
    { title: "Проверьте и сохраните", short: "Готовая таблица", icon: Save, text: "Проверьте распознанные наименования, количество и цены. Исправьте неточности, задайте понятное имя документа и сохраните его в базе." },
    { title: "Перейдите в анализ цен", short: "Вкладка в шапке", icon: GitCompareArrows, text: "После сохранения запроса и предложений откройте вкладку «Анализ цен» в верхней панели приложения." },
    { title: "Выберите документы", short: "Запрос + несколько КП", icon: ClipboardList, text: "В блоке «Запросы» выберите один документ заказчика, а в блоке «Предложения» — одно или несколько коммерческих предложений поставщиков. Важно: позиции сопоставляются только при точном совпадении артикула." },
    { title: "Сформируйте Excel", short: "Сравнение цен", icon: FileSpreadsheet, text: "Нажмите «Сравнение цен». Система сопоставит позиции и сформирует Excel-документ с ценами поставщиков. Для просмотра результата в интерфейсе используйте «Запустить анализ»." },
];

const GuidePreview = ({ step }) => (
    <div className={styles.guidePreview} aria-label="Пример экрана приложения">
        <div className={styles.guidePreviewTopbar}><i /><i /><i /><span>SYM · рабочая область</span></div>
        {step === 0 && (
            <div className={styles.guideProcessorMock}>
                <div className={styles.guideMockTabs}><b>Обработать запрос</b><span>Обработать предложение</span></div>
                <div className={styles.guideMockColumns}>
                    <div><em>Текст</em><span className={styles.guideTextarea}>STM32F103&nbsp;&nbsp; 25 шт.<br />AD620ARZ&nbsp;&nbsp; 10 шт.<br />LM358&nbsp;&nbsp; 50 шт.</span><button>Обработать</button></div>
                    <div className={styles.guideScanMock}><FileText size={28} /><i /><strong>Разбираем данные</strong></div>
                </div>
            </div>
        )}
        {step === 1 && (
            <div className={styles.guideResultMock}>
                <div><strong>Результат обработки</strong><span>Таблица</span></div>
                <div className={styles.guideMiniTable}>
                    <b>Наименование</b><b>Кол-во</b><b>Цена</b>
                    <span>STM32F103</span><span>25</span><span>—</span>
                    <span>AD620ARZ</span><span>10</span><span>—</span>
                    <span>LM358</span><span>50</span><span>—</span>
                </div>
                <div className={styles.guideSaveRow}><span>Запрос_03.08</span><button><Save size={12} /> Сохранить в БД</button></div>
            </div>
        )}
        {step === 2 && (
            <div className={styles.guideNavigationMock}>
                <div><span>Обработка документа</span><b><Sparkles size={15} /> Анализ цен</b><span>Аналитика</span></div>
                <ArrowMarker />
                <section><GitCompareArrows size={34} /><strong>Переходим к сравнению</strong><small>Сохранённые документы уже доступны для выбора</small></section>
            </div>
        )}
        {step === 3 && (
            <div className={styles.guideSelectionMock}>
                <div className={styles.guideSelectionStatus}><Sparkles size={17} /><strong>Готово к анализу</strong><span>1 запрос · 2 предложения</span></div>
                <div className={styles.guideDocumentColumns}>
                    <section><b>Запросы</b><p className={styles.guideSelectedDoc}><CheckCircle2 size={14} /> Запрос_03.08.xlsx</p><p>Запрос_01.08.xlsx</p></section>
                    <section><b>Предложения</b><p className={styles.guideSelectedDoc}><CheckCircle2 size={14} /> Поставщик Альфа</p><p className={styles.guideSelectedDoc}><CheckCircle2 size={14} /> Поставщик Бета</p></section>
                </div>
            </div>
        )}
        {step === 4 && (
            <div className={styles.guideExportMock}>
                <div className={styles.guideSelectionStatus}><strong>1 запрос · 2 предложения</strong></div>
                <div className={styles.guideExportButtons}><button><FileSpreadsheet size={16} /> Сравнение цен</button><button>Запустить анализ</button></div>
                <div className={styles.guideExcelFile}><FileSpreadsheet size={32} /><div><strong>Сравнение_цен.xlsx</strong><span>Excel-документ сформирован</span></div><Download size={18} /></div>
            </div>
        )}
    </div>
);

const ArrowMarker = () => <div className={styles.guideArrowMarker}><span>↓</span></div>;

export default function HeaderTabs({
    session,
    activeTab,
    setActiveTab,
    onOpenRowsSearchModal,
}) {
    const navigate = useNavigate();
    const [analysisGuideOpen, setAnalysisGuideOpen] = useState(false);
    const [analysisGuideStep, setAnalysisGuideStep] = useState(0);
    const ActiveGuideIcon = ANALYSIS_GUIDE[analysisGuideStep].icon;

    return (
        <>
            <div className={styles.tabHeader}>

                <div
                    className={`${styles.tabItem} ${activeTab === 'new-analysis' ? styles.activeTab : ''}`}
                    onClick={() => setActiveTab('new-analysis')}
                >
                    <Sparkles size={16} />
                    <span className={styles.tabText}>Анализ цен</span>
                </div>

                <div
                    className={`${styles.tabItem} ${activeTab === 'doc' ? styles.activeTab : ''}`}
                    onClick={() => setActiveTab('doc')}
                >
                    <FileText size={16} />
                    <span className={styles.tabText}>Обработка документа</span>
                </div>

                <button type="button" className={`${styles.tabItem} ${activeTab === 'catalog-search' ? styles.activeTab : ''}`} onClick={() => setActiveTab('catalog-search')}><Search size={16} /><span className={styles.tabText}>Поиск номенклатуры</span></button>

                {activeTab === "new-analysis" && (
                    <button
                        type="button"
                        className={styles.headerInstructionButton}
                        onClick={() => {
                            setAnalysisGuideStep(0);
                            setAnalysisGuideOpen(true);
                        }}
                    >
                        <BookOpen size={16} />
                        Инструкция
                    </button>
                )}

                <button
                    className={styles.rowsSearchButton}
                    onClick={onOpenRowsSearchModal}
                >
                    <Search size={16} />
                    Поиск компонента
                </button>

                <div
                    className={`${styles.tabItem} ${activeTab === 'analytics' ? styles.activeTab : ''}`}
                    onClick={() => setActiveTab('analytics')}
                >
                    <BarChart3 size={16} />
                    <span className={styles.tabText}>Аналитика</span>
                </div>

                <div className={styles.buttonsContainer}>
                    <ProfileControl session={session} className={styles.profileButton} />

                    <button
                        className={styles.iconHeaderButton}
                        type="button"
                        onClick={() => navigate('/sym/admin')}
                        title="Администрирование"
                        aria-label="Администрирование"
                    >
                        <Settings size={19} strokeWidth={2.2} aria-hidden="true" />
                    </button>

                </div>
            </div>

            <Modal
                open={analysisGuideOpen}
                onCancel={() => setAnalysisGuideOpen(false)}
                footer={null}
                width={1080}
                title={null}
                className={styles.analysisGuideModal}
            >
                <div className={styles.analysisGuideHeader}>
                    <div className={styles.analysisGuideHeaderIcon}><Sparkles size={22} /></div>
                    <div>
                        <span>Инструкция</span>
                        <h2>Как выполнить анализ цен</h2>
                        <p>Выбирайте этап слева — справа появится его описание.</p>
                    </div>
                </div>
                <div className={styles.analysisGuideMatchNotice}>
                    <strong>Важно</strong>
                    <span>Сопоставление номенклатуры выполняется по точному совпадению артикула в запросе и предложении.</span>
                </div>
                <div className={styles.analysisGuideBody}>
                    <nav className={styles.analysisGuideSteps} aria-label="Этапы анализа цен">
                        {ANALYSIS_GUIDE.map((item, index) => {
                            const Icon = item.icon;
                            return (
                                <button
                                    type="button"
                                    key={item.title}
                                    className={analysisGuideStep === index ? styles.analysisGuideStepActive : ""}
                                    onClick={() => setAnalysisGuideStep(index)}
                                >
                                    <span><Icon size={17} /></span>
                                    <div><strong>{item.title}</strong><small>{item.short}</small></div>
                                </button>
                            );
                        })}
                    </nav>
                    <section className={styles.analysisGuideDescription} key={analysisGuideStep}>
                        <div className={styles.analysisGuideText}>
                            <div><ActiveGuideIcon size={25} /></div>
                            <span>Шаг {analysisGuideStep + 1} из {ANALYSIS_GUIDE.length}</span>
                            <h3>{ANALYSIS_GUIDE[analysisGuideStep].title}</h3>
                            <p>{ANALYSIS_GUIDE[analysisGuideStep].text}</p>
                        </div>
                        <GuidePreview step={analysisGuideStep} />
                        <button
                            type="button"
                            onClick={() => analysisGuideStep < ANALYSIS_GUIDE.length - 1
                                ? setAnalysisGuideStep(step => step + 1)
                                : setAnalysisGuideOpen(false)}
                        >
                            {analysisGuideStep < ANALYSIS_GUIDE.length - 1 ? "Следующий шаг" : "ОК"}
                        </button>
                    </section>
                </div>
            </Modal>
        </>
    );
}
